import {
  AssetFolder,
  ChatbotsOrderBy,
  ChatbotsOrderDirection,
} from "@zapier/bff";
import {
  HydratedChatbot,
  useChatbots,
  useCurrentUser,
  useFoldersLookup,
  useSupportedAssets,
} from "@zapier/client";
import { SortDescriptor } from "@zapier/design-system";
import { useRouterParams } from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";
import { useEffect, useMemo, useState } from "react";

interface Input {
  pageSize?: number;
}

export type HydratedChatbotWithFolder = HydratedChatbot & {
  folder?: AssetFolder;
  isLocked?: boolean;
};

// Check to see if the order value is a valid orderBy value
const isChatbotOrderBy = (
  order: string | null | undefined,
): order is ChatbotsOrderBy => {
  const validOrders: ChatbotsOrderBy[] = [
    "id",
    "name",
    "createdAt",
    "updatedAt",
  ];
  const parsedOrder = order?.replace("-", "");
  return (
    order !== null &&
    validOrders.includes(parsedOrder as unknown as ChatbotsOrderBy)
  );
};

const getOrderDirection = (order?: string | null): ChatbotsOrderDirection => {
  if (order && isChatbotOrderBy(order)) {
    return order?.startsWith("-") ? "desc" : "asc";
  }

  return "desc";
};

export function useChatbotsList(input?: Input) {
  const {
    order,
    owner: selectedOwner,
    page,
    search,
    setParams,
    shared,
  } = useRouterParams();
  const { isChatbotsEnabled } = useSupportedAssets();
  const { user, isLoading: isUserLoading } = useCurrentUser();

  /** Shared filtering selection **************************************** */

  const userId = user?.id ?? "";
  let owner: string | undefined | null;

  if (selectedOwner) {
    owner = selectedOwner;
  } else if (shared && (isUserLoading || !userId)) {
    // eslint-disable-next-line unicorn/no-null
    owner = null;
  } else if (shared === "me") {
    owner = String(userId);
  } else if (shared === "-me") {
    owner = `-${userId}`;
  }

  /** Sorting *********************************************** */
  const sortChange = (descriptor: SortDescriptor) => {
    const orderChange = `${descriptor.direction === "ascending" ? "" : "-"}${descriptor.column}`;
    if (orderChange) {
      setParams({ order: orderChange, page: 1 });
    }
  };

  /** Fetch chatbots *********************************************** */
  const { chatbots, isLoading, isFetching, meta, loadError } = useChatbots({
    orderBy: isChatbotOrderBy(order)
      ? (order.replace("-", "") as ChatbotsOrderBy)
      : "updatedAt",
    orderDirection: getOrderDirection(order),
    owner,
    page,
    pageSize: input?.pageSize,
    search,
  });

  /** Loading state  *********************************************************
   * only show loading when filters change */

  const [didFiltersChange, setDidFiltersChange] = useState(true);

  useEffect(() => {
    setDidFiltersChange(true);
  }, [search, selectedOwner, shared, page]);

  useEffect(() => {
    if (!isLoading) {
      setDidFiltersChange(false);
    }
  }, [isLoading]);

  const chatbotIds = useMemo(
    () => chatbots?.map((chatbot) => chatbot.id || "") || [],
    [chatbots],
  );

  const { folderLookup } = useFoldersLookup({
    assetType: AssetTypes.Chatbot,
    assetIds: chatbotIds,
    enabled: chatbotIds.length > 0 && isChatbotsEnabled,
  });

  const hydratedChatbots = useMemo(() => {
    return (
      chatbots?.map((chatbot) => ({
        ...chatbot,
        folder: folderLookup[chatbot.id || ""],
      })) || []
    );
  }, [chatbots, folderLookup]);

  return {
    loadError,
    chatbots: hydratedChatbots,
    isLoading,
    isFetching: isFetching && didFiltersChange,
    order,
    page: page ?? 1,
    sortChange,
    totalPages: Math.ceil((meta?.count ?? 0) / (meta?.limit || 1)),
    totalResults: meta?.count ?? 0,
  };
}
